"use client";

import { c as _c } from "react/compiler-runtime";
import { resolveEventOption, type EventText } from "@/lib/event-option";
import { useT, useGameLocale, type TFn } from "@/lib/i18n";
import type { ReactNode } from "react";
import { imageUrl } from "@/lib/image-url";
import type { ScoresMap } from "@/lib/use-entity-scores";
import type { BuyLine, HitLine, ReplayCombat, ReplayDecision, ReplayFloor, ReplayLine, ReplayOption, ReplayTurn, ShopItem } from "@/lib/replay";
import { isCombatKind } from "@/lib/replay";
import { type EncounterMap, type MonsterMap, LiveCardImg, safeId } from "@/app/[locale]/live/live-shared";
import { CardPill, PotionPill, RelicPill, cleanId, displayName, type CardInfo, type PotionInfo, type RelicInfo } from "../RunPills";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import type { TryGameT } from "@/lib/game-i18n";
export interface EventInfo extends EventText {
  id: string;
  name: string;
}
export interface Catalog {
  cards: Record<string, CardInfo>;
  relics: Record<string, RelicInfo>;
  potions: Record<string, PotionInfo>;
  events: Record<string, EventInfo>;
  monsters: MonsterMap;
  encounters: EncounterMap;
  cardScores: ScoresMap;
  relicScores: ScoresMap;
  gt?: TryGameT;
}
export const KIND_LABEL: Record<string, string> = {
  combat: "Combat",
  monster: "Combat",
  elite: "Elite",
  boss: "Boss",
  merchant: "Shop",
  shop: "Shop",
  restsite: "Rest site",
  rest: "Rest site",
  treasure: "Treasure",
  event: "Event",
  unknown: "Unknown",
  ancient: "Ancient"
};
export function cardName(id: string, cat: Catalog): string {
  return cat.gt?.(`cards.${id}.title`) || cat.cards[id]?.name || displayName(`CARD.${id}`);
}
export function relicName(id: string, cat: Catalog): string {
  return cat.gt?.(`relics.${id}.title`) || cat.relics[id]?.name || displayName(`RELIC.${id}`);
}
export function potionName(id: string, cat: Catalog): string {
  return cat.gt?.(`potions.${id}.title`) || cat.potions[id]?.name || displayName(`POTION.${id}`);
}
export function monsterName(id: string, cat: Catalog): string {
  return cat.gt?.(`monsters.${cleanId(id)}.name`) || cat.monsters[cleanId(id)]?.name || displayName(`MONSTER.${id}`);
}
export function moveName(monsterId: string, moveId: string, cat: Catalog): string {
  return cat.gt?.(`monsters.${cleanId(monsterId)}.moves.${moveId}.title`) || cat.monsters[cleanId(monsterId)]?.moves?.find(m => m.id === moveId)?.name || displayName(moveId);
}
const PILL = "font-semibold text-[var(--text-primary)] underline decoration-[var(--border-subtle)] underline-offset-2 hover:decoration-[var(--accent-gold)]";
function Card(t0) {
  const $ = _c(5);
  const {
    id,
    up,
    cat,
    bp
  } = t0;
  let t1;
  if ($[0] !== bp || $[1] !== cat.cards || $[2] !== id || $[3] !== up) {
    t1 = <CardPill cardId={id} upgraded={up} cardData={cat.cards} bp={bp} className={PILL} />;
    $[0] = bp;
    $[1] = cat.cards;
    $[2] = id;
    $[3] = up;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function Relic(t0) {
  const $ = _c(4);
  const {
    id,
    cat,
    bp
  } = t0;
  let t1;
  if ($[0] !== bp || $[1] !== cat.relics || $[2] !== id) {
    t1 = <RelicPill relicId={id} relicData={cat.relics} bp={bp} className={PILL} />;
    $[0] = bp;
    $[1] = cat.relics;
    $[2] = id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function Potion(t0) {
  const $ = _c(4);
  const {
    id,
    cat,
    bp
  } = t0;
  let t1;
  if ($[0] !== bp || $[1] !== cat.potions || $[2] !== id) {
    t1 = <PotionPill potionId={id} potionData={cat.potions} bp={bp} className={PILL} />;
    $[0] = bp;
    $[1] = cat.potions;
    $[2] = id;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}

/** t() with one entity pill substituted for the {card}/{item}/{potion} slot. */
function withPill(text: string, slot: string, pill: ReactNode): ReactNode {
  const i = text.indexOf(slot);
  if (i < 0) return text;
  return <>
      {text.slice(0, i)}
      {pill}
      {text.slice(i + slot.length)}
    </>;
}
export function encounterName(id: string | undefined, cat: Catalog): string {
  if (!id) return "";
  return cat.gt?.(`encounters.${id}.title`) || cat.encounters[id]?.name || displayName(`ENCOUNTER.${id}`);
}
export function floorTitle(f: ReplayFloor, cat: Catalog, t: TFn): string {
  if (isCombatKind(f.kind)) return encounterName(f.id, cat) || t("Combat");
  if (f.kind === "event" && f.id) return cat.gt?.(`events.${cleanId(f.id)}.title`) || cat.events[cleanId(f.id)]?.name || displayName(`EVENT.${f.id}`);
  return t(KIND_LABEL[f.kind] ?? displayName(f.kind));
}
function ScoreChip(t0) {
  const $ = _c(9);
  const {
    id,
    scores
  } = t0;
  const sc = scores[id.toUpperCase()];
  if (!sc || sc.score === null) {
    return null;
  }
  let t1;
  if ($[0] !== sc.score) {
    t1 = <span title="Codex score">{sc.score}</span>;
    $[0] = sc.score;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== sc.win_rate) {
    t2 = sc.win_rate.toFixed(0);
    $[2] = sc.win_rate;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <span title="Win rate at this bracket">{t2}%</span>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <span className="ml-auto flex items-center gap-2 text-[10px] tabular-nums text-[var(--text-muted)]">{t1}{t3}</span>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
const MARKUP_TONE: Record<string, string> = {
  gold: "text-[var(--accent-gold)]",
  red: "text-[var(--accent-red)]",
  green: "font-semibold text-[var(--text-primary)]",
  blue: "font-semibold text-[var(--text-primary)]"
};

// The game's option text uses [gold]..[/gold] style tags; color the ones
// the map colors use and drop the rest (images, unknown tags).
function Markup(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  let t1;
  if ($[0] !== text) {
    const parts = [];
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = /\[(\/?)([a-z]+)[^\]]*\]/gi;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    const re = t2;
    const stack = [];
    let last = 0;
    let m;
    const flush = end => {
      if (end <= last) {
        return;
      }
      const chunk = text.slice(last, end);
      const tone = stack.length ? MARKUP_TONE[stack[stack.length - 1]] : undefined;
      parts.push(tone ? <span key={parts.length} className={tone}>{chunk}</span> : chunk);
    };
    while (m = re.exec(text)) {
      flush(m.index);
      const tag = m[2].toLowerCase();
      if (tag in MARKUP_TONE) {
        if (m[1]) {
          const i = stack.lastIndexOf(tag);
          if (i >= 0) {
            stack.splice(i, 1);
          }
        } else {
          stack.push(tag);
        }
      }
      last = re.lastIndex;
    }
    flush(text.length);
    t1 = <>{parts}</>;
    $[0] = text;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function itemName(kind: string, id: string, cat: Catalog): string {
  if (kind === "card") return cardName(id, cat);
  if (kind === "relic") return relicName(id, cat);
  if (kind === "potion") return potionName(id, cat);
  return displayName(id);
}
function costLabel(l: BuyLine): string {
  return `${l.costCurrent ?? "?"} ${l.costResource}`;
}
const SHOP_KINDS = new Set(["merchant", "shop"]);
export function isShopKind(kind: string): boolean {
  return SHOP_KINDS.has(kind);
}
interface Described {
  key: string;
  node: ReactNode;
}
const SLOT = "\u0000";
function describeLine(l: ReplayLine, cat: Catalog, t: TFn, who: string, bp: string, healed?: number): Described | undefined {
  const pill = (key: string, values: Record<string, string | number>, slot: string, node: ReactNode): Described => {
    const text = t(key, {
      ...values,
      [slot]: SLOT
    });
    return {
      key: t(key, values),
      node: withPill(text, SLOT, node)
    };
  };
  const plain = (text: string): Described => ({
    key: text,
    node: text
  });
  switch (l.t) {
    case "relic":
      return pill("Got {item}", {
        item: relicName(l.id, cat)
      }, "item", <Relic id={l.id} cat={cat} bp={bp} />);
    case "potion_got":
      return pill("Got {item}", {
        item: potionName(l.id, cat)
      }, "item", <Potion id={l.id} cat={cat} bp={bp} />);
    case "acquire":
      return pill("Took {card}", {
        card: cardName(l.id, cat)
      }, "card", <Card id={l.id} cat={cat} bp={bp} />);
    case "upgrade":
      return pill("Upgraded {card}", {
        card: cardName(l.id, cat)
      }, "card", <Card id={l.id} up cat={cat} bp={bp} />);
    case "remove":
      return pill("Removed {card}", {
        card: cardName(l.id, cat)
      }, "card", <Card id={l.id} cat={cat} bp={bp} />);
    case "transform":
      return {
        key: `${cardName(l.fromId, cat)} → ${cardName(l.toId, cat)}`,
        node: <>
            <Card id={l.fromId} cat={cat} bp={bp} /> →{" "}
            <Card id={l.toId} cat={cat} bp={bp} />
          </>
      };
    case "rest":
      if (l.option === "heal") return plain(healed ? t("{who} healed for {n} HP", {
        who,
        n: healed
      }) : t("{who} rested and healed", {
        who
      }));
      if (l.option === "smith") return plain(t("{who} smithed at the campfire", {
        who
      }));
      return plain(t("{who} chose {option} at the campfire", {
        who,
        option: displayName(l.option ?? "")
      }));
    case "buy":
      if (l.kind === "removal_service") return plain(`${t("Card removal")} (${costLabel(l)})`);
      if (l.kind === "card" && l.id) return {
        key: `${t("Bought")} ${cardName(l.id, cat)}`,
        node: <>
              {t("Bought")} <Card id={l.id} cat={cat} bp={bp} /> ({costLabel(l)}
              )
            </>
      };
      if (l.kind === "relic" && l.id) return {
        key: `${t("Bought")} ${relicName(l.id, cat)}`,
        node: <>
              {t("Bought")} <Relic id={l.id} cat={cat} bp={bp} /> (
              {costLabel(l)})
            </>
      };
      if (l.kind === "potion" && l.id) return {
        key: `${t("Bought")} ${potionName(l.id, cat)}`,
        node: <>
              {t("Bought")} <Potion id={l.id} cat={cat} bp={bp} /> (
              {costLabel(l)})
            </>
      };
      return plain(`${t("Bought")} ${itemName(l.kind, l.id ?? "", cat)} (${costLabel(l)})`);
    case "hp":
      if (!l.d) return undefined;
      return plain(l.d > 0 ? t("{who} healed for {n} HP", {
        who,
        n: l.d
      }) : t("{who} lost {n} HP", {
        who,
        n: -l.d
      }));
    case "hp_loss":
      return l.dmg ? plain(t("{who} lost {n} HP", {
        who,
        n: Math.abs(l.dmg)
      })) : undefined;
    case "resume":
      return plain(`${t("Reloaded from a save")}${l.reloads > 1 ? ` (${l.reloads})` : ""}`);
    case "pick":
      if (!l.cards.length) return undefined;
      if (l.cards.length === 1) return pill("Chose {card}", {
        card: cardName(l.cards[0].id, cat)
      }, "card", <Card id={l.cards[0].id} up={!!l.cards[0].up} cat={cat} bp={bp} />);
      return {
        key: `${t("Chose")}: ${l.cards.map(c => cardName(c.id, cat)).join(", ")}`,
        node: <>
            {t("Chose")}:{" "}
            {l.cards.map((c, j) => <span key={`${l.s}-${j}`}>
                {j > 0 && ", "}
                <Card id={c.id} up={!!c.up} cat={cat} bp={bp} />
              </span>)}
          </>
      };
    default:
      return undefined;
  }
}
function Delta(t0) {
  const $ = _c(4);
  const {
    value
  } = t0;
  if (!value) {
    return null;
  }
  const t1 = `ml-1 ${value > 0 ? "text-[var(--accent-gold)]" : "text-[var(--accent-red)]"}`;
  const t2 = value > 0 ? "+" : "";
  let t3;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== value) {
    t3 = <span className={t1}>({t2}{value})</span>;
    $[0] = t1;
    $[1] = t2;
    $[2] = value;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
const TAKEN_TONE = "border-[var(--accent-gold)] bg-[color-mix(in_srgb,var(--accent-gold)_12%,transparent)] text-[var(--text-primary)]";
function TakenBadge() {
  const $ = _c(4);
  const t = useT();
  let t0;
  if ($[0] !== t) {
    t0 = t("Taken");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = <span className="ml-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-gold)]">{t0}</span>;
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function OptionRow(t0) {
  const $ = _c(66);
  const {
    o,
    dec,
    cat
  } = t0;
  const t = useT();
  useGameLocale();
  const isCard = o.kind === "card" || o.kind === "remove" || o.kind === "transform" || o.kind === "upgrade" || o.kind === "enchant";
  const isRelic = o.kind === "relic" || !!o.grantsRelic;
  const t1 = o.kind === "event_option" ? o.id : undefined;
  let t2;
  if ($[0] !== cat.relics) {
    t2 = id => cat.relics[id];
    $[0] = cat.relics;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== cat.events || $[3] !== o.desc || $[4] !== o.grantsRelic || $[5] !== o.label || $[6] !== t || $[7] !== t1 || $[8] !== t2) {
    t3 = resolveEventOption(t1, cat.events, t2, t, {
      label: o.label,
      desc: o.desc,
      grantsRelic: o.grantsRelic
    });
    $[2] = cat.events;
    $[3] = o.desc;
    $[4] = o.grantsRelic;
    $[5] = o.label;
    $[6] = t;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const resolved = t3;
  const desc = resolved ? resolved.desc : o.desc;
  let t4;
  if ($[10] !== cat || $[11] !== isCard || $[12] !== isRelic || $[13] !== o.grantsRelic || $[14] !== o.id || $[15] !== o.kind || $[16] !== o.label || $[17] !== o.upgraded || $[18] !== resolved) {
    t4 = resolved ? resolved.label : o.label ? o.label : isCard ? `${cardName(o.id, cat)}${o.upgraded ? "+" : ""}` : isRelic ? relicName(o.grantsRelic || o.id, cat) : o.kind === "potion" ? potionName(o.id, cat) : displayName(o.id);
    $[10] = cat;
    $[11] = isCard;
    $[12] = isRelic;
    $[13] = o.grantsRelic;
    $[14] = o.id;
    $[15] = o.kind;
    $[16] = o.label;
    $[17] = o.upgraded;
    $[18] = resolved;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const label = t4;
  const tone = o.chosen ? TAKEN_TONE : o.selectable ? "border-[var(--border-subtle)] text-[var(--text-secondary)]" : "border-[var(--border-subtle)] text-[var(--text-muted)] opacity-60";
  const t5 = `flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-sm ${tone}`;
  let t6;
  if ($[20] !== cat.cards || $[21] !== isCard || $[22] !== label || $[23] !== o.id || $[24] !== o.upgraded) {
    t6 = isCard && safeId(o.id) && <LiveCardImg id={o.id} upgraded={o.upgraded} alt={label} className="h-9 w-auto rounded-sm" portrait={cat.cards[o.id]?.image_url} />;
    $[20] = cat.cards;
    $[21] = isCard;
    $[22] = label;
    $[23] = o.id;
    $[24] = o.upgraded;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== cat.relics || $[27] !== isRelic || $[28] !== o.grantsRelic || $[29] !== o.id) {
    t7 = isRelic && <img src={imageUrl(cat.relics[o.grantsRelic || o.id]?.image_url || `/static/images/relics/${(o.grantsRelic || o.id).toLowerCase()}.png`)} alt="" className="h-7 w-7 object-contain" loading="lazy" />;
    $[26] = cat.relics;
    $[27] = isRelic;
    $[28] = o.grantsRelic;
    $[29] = o.id;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== label) {
    t8 = <span className="block truncate">{label}</span>;
    $[31] = label;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== desc) {
    t9 = desc && <span className="block text-xs leading-snug text-[var(--text-muted)]"><Markup text={desc} /></span>;
    $[33] = desc;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== t8 || $[36] !== t9) {
    t10 = <span className="min-w-0 flex-1">{t8}{t9}</span>;
    $[35] = t8;
    $[36] = t9;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== o.chosen || $[39] !== t) {
    t11 = o.chosen && <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-gold)]">{t("Taken")}</span>;
    $[38] = o.chosen;
    $[39] = t;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== o.reason || $[42] !== o.selectable) {
    t12 = !o.selectable && o.reason && <span className="text-[10px] uppercase tracking-wider">{o.reason}</span>;
    $[41] = o.reason;
    $[42] = o.selectable;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== cat.cardScores || $[45] !== isCard || $[46] !== o.id) {
    t13 = isCard && <ScoreChip id={o.id} scores={cat.cardScores} />;
    $[44] = cat.cardScores;
    $[45] = isCard;
    $[46] = o.id;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== cat.relicScores || $[49] !== isRelic || $[50] !== o.grantsRelic || $[51] !== o.id) {
    t14 = isRelic && <ScoreChip id={o.grantsRelic || o.id} scores={cat.relicScores} />;
    $[48] = cat.relicScores;
    $[49] = isRelic;
    $[50] = o.grantsRelic;
    $[51] = o.id;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== dec.paid || $[54] !== o.chosen) {
    t15 = dec.paid && o.chosen && dec.paid.kind !== "removal_service" && dec.paid.cost !== undefined && <span className="text-[10px] text-[var(--text-muted)]">{dec.paid.cost} {dec.paid.resource}</span>;
    $[53] = dec.paid;
    $[54] = o.chosen;
    $[55] = t15;
  } else {
    t15 = $[55];
  }
  let t16;
  if ($[56] !== t10 || $[57] !== t11 || $[58] !== t12 || $[59] !== t13 || $[60] !== t14 || $[61] !== t15 || $[62] !== t5 || $[63] !== t6 || $[64] !== t7) {
    t16 = <li className={t5}>{t6}{t7}{t10}{t11}{t12}{t13}{t14}{t15}</li>;
    $[56] = t10;
    $[57] = t11;
    $[58] = t12;
    $[59] = t13;
    $[60] = t14;
    $[61] = t15;
    $[62] = t5;
    $[63] = t6;
    $[64] = t7;
    $[65] = t16;
  } else {
    t16 = $[65];
  }
  return t16;
}
function decisionTitle(d: ReplayDecision, cat: Catalog, t: TFn): string {
  if (d.type === "card_reward") return "Reward";
  if (d.selectKind === "transform") return "Transform a card";
  if (d.selectKind === "upgrade") return "Upgrade a card";
  if (d.selectKind === "enchant") return "Enchant a card";
  if (d.selectKind === "remove" || d.paid?.kind === "removal_service") return "Remove a card";
  if (d.type === "event") return d.eventId ? cat.events[cleanId(d.eventId)]?.name || displayName(`EVENT.${d.eventId}`) : "Event";
  if (d.type === "relic_reward") return "Relic reward";
  if (d.type === "potion_reward") return "Potion reward";
  return displayName(d.type);
}
function DecisionCard(t0) {
  const $ = _c(50);
  const {
    d,
    cat
  } = t0;
  const t = useT();
  useGameLocale();
  const bp = useBetaPrefix();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== bp || $[1] !== cat || $[2] !== d || $[3] !== t) {
    const shown = d.options.filter(_temp);
    const picked = shown.some(_temp2);
    const gold = d.resolutions.reduce(_temp3, 0);
    const potions = d.resolutions.flatMap(_temp4);
    const relics = d.resolutions.flatMap(l_1 => l_1.t === "relic" && !shown.some(o_1 => o_1.id === l_1.id || o_1.grantsRelic === l_1.id) ? [l_1.id] : []);
    const rewards = gold > 0 || potions.length > 0 || relics.length > 0;
    t3 = "rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] p-3";
    let t6;
    if ($[9] !== cat || $[10] !== d || $[11] !== t) {
      t6 = decisionTitle(d, cat, t);
      $[9] = cat;
      $[10] = d;
      $[11] = t;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t6) {
      t7 = <h4 className="text-sm font-semibold text-[var(--text-primary)]">{t6}</h4>;
      $[13] = t6;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    const t8 = shown.length;
    let t9;
    if ($[15] !== t) {
      t9 = t("offered");
      $[15] = t;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== d.nPresented || $[18] !== d.nSelectable || $[19] !== t) {
      t10 = d.nSelectable !== undefined && d.nPresented !== undefined && d.nSelectable < d.nPresented && ` · ${d.nSelectable} ${t("selectable")}`;
      $[17] = d.nPresented;
      $[18] = d.nSelectable;
      $[19] = t;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== d.goldOnHand || $[22] !== t) {
      t11 = d.goldOnHand !== undefined && ` · ${d.goldOnHand} ${t("gold")}`;
      $[21] = d.goldOnHand;
      $[22] = t;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] !== d.outcome || $[25] !== t) {
      t12 = d.outcome === "skip" && <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">{t("Skipped")}</span>;
      $[24] = d.outcome;
      $[25] = t;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    let t13;
    if ($[27] !== d.outcome || $[28] !== t) {
      t13 = d.outcome === "reroll" && <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">{t("Rerolled")}</span>;
      $[27] = d.outcome;
      $[28] = t;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== d.selectionStatus || $[31] !== t) {
      t14 = d.selectionStatus === "partial" && <span className="text-xs uppercase tracking-wider text-[var(--text-muted)]">{t("Some picks not recorded")}</span>;
      $[30] = d.selectionStatus;
      $[31] = t;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    let t15;
    if ($[33] !== d.selectionStatus || $[34] !== t) {
      t15 = d.selectionStatus === "conflict" && <span className="text-xs uppercase tracking-wider text-[var(--accent-red)]">{t("Conflicting choice records")}</span>;
      $[33] = d.selectionStatus;
      $[34] = t;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    let t16;
    if ($[36] !== d.paid || $[37] !== t) {
      t16 = d.paid && d.paid.kind === "removal_service" && <span className="text-xs text-[var(--text-muted)]">{t("Paid")} {d.paid.cost} {d.paid.resource}</span>;
      $[36] = d.paid;
      $[37] = t;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    t4 = <header className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">{t7}<span className="text-xs text-[var(--text-muted)]">{t8} {t9}{t10}{t11}</span>{t12}{t13}{d.selectionStatus === "unknown" && !picked && d.outcome !== "skip" && d.outcome !== "reroll" && <span className="text-xs uppercase tracking-wider text-[var(--text-muted)]">{t("Chosen option not recorded")}</span>}{t14}{t15}{t16}</header>;
    t5 = rewards && <ul className="mb-2 flex flex-wrap items-stretch gap-1.5">{gold > 0 && <li className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm tabular-nums ${TAKEN_TONE}`}><img src={imageUrl("/static/images/icons/gold_icon.webp")} alt={t("gold")} className="h-4 w-4" loading="lazy" /><span className="text-[var(--accent-gold)]">{gold}</span><TakenBadge /></li>}{potions.map((id, i) => <li key={`p-${id}-${i}`} className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm ${TAKEN_TONE}`}><img src={imageUrl(cat.potions[id]?.image_url || `/static/images/potions/${id.toLowerCase()}.webp`)} alt="" className="h-7 w-7 object-contain" loading="lazy" /><Potion id={id} cat={cat} bp={bp} /><TakenBadge /></li>)}{relics.map((id_0, i_0) => <li key={`r-${id_0}-${i_0}`} className={`inline-flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm ${TAKEN_TONE}`}><img src={imageUrl(cat.relics[id_0]?.image_url || `/static/images/relics/${id_0.toLowerCase()}.png`)} alt="" className="h-7 w-7 object-contain" loading="lazy" /><Relic id={id_0} cat={cat} bp={bp} /><TakenBadge /></li>)}</ul>;
    t1 = "grid gap-1.5 sm:grid-cols-2";
    let t17;
    if ($[39] !== cat || $[40] !== d) {
      t17 = o_2 => <OptionRow key={`${d.id}-${o_2.index}`} o={o_2} dec={d} cat={cat} />;
      $[39] = cat;
      $[40] = d;
      $[41] = t17;
    } else {
      t17 = $[41];
    }
    t2 = shown.map(t17);
    $[0] = bp;
    $[1] = cat;
    $[2] = d;
    $[3] = t;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[42] !== t1 || $[43] !== t2) {
    t6 = <ul className={t1}>{t2}</ul>;
    $[42] = t1;
    $[43] = t2;
    $[44] = t6;
  } else {
    t6 = $[44];
  }
  let t7;
  if ($[45] !== t3 || $[46] !== t4 || $[47] !== t5 || $[48] !== t6) {
    t7 = <section className={t3}>{t4}{t5}{t6}</section>;
    $[45] = t3;
    $[46] = t4;
    $[47] = t5;
    $[48] = t6;
    $[49] = t7;
  } else {
    t7 = $[49];
  }
  return t7;
}
function _temp4(l_0) {
  return l_0.t === "resolve" && l_0.rewardKind === "PotionReward" && l_0.id ? [l_0.id] : [];
}
function _temp3(n, l) {
  return n + (l.t === "resolve" && l.rewardKind === "GoldReward" && l.gold ? l.gold : 0);
}
function _temp2(o_0) {
  return o_0.chosen;
}
function _temp(o) {
  return o.presented;
}
function boughtSlots(buys: BuyLine[], kind: string): Set<number> {
  return new Set(buys.filter(b => b.kind === kind && b.slot !== undefined).map(b => b.slot as number));
}
function StockRow(t0) {
  const $ = _c(47);
  const {
    item,
    kind,
    bought,
    cat
  } = t0;
  const t = useT();
  useGameLocale();
  let t1;
  if ($[0] !== cat || $[1] !== item.id || $[2] !== kind) {
    t1 = itemName(kind, item.id, cat);
    $[0] = cat;
    $[1] = item.id;
    $[2] = kind;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const name = t1;
  const tone = bought ? "border-[var(--accent-gold)] bg-[color-mix(in_srgb,var(--accent-gold)_12%,transparent)] text-[var(--text-primary)]" : "border-[var(--border-subtle)] text-[var(--text-secondary)]";
  const t2 = `flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm ${tone}`;
  let t3;
  if ($[4] !== cat || $[5] !== item.id || $[6] !== kind || $[7] !== name) {
    t3 = kind === "card" && safeId(item.id) && <LiveCardImg id={item.id} alt={name} className="h-9 w-auto rounded-sm" portrait={cat.cards[item.id]?.image_url} />;
    $[4] = cat;
    $[5] = item.id;
    $[6] = kind;
    $[7] = name;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== cat || $[10] !== item.id || $[11] !== kind) {
    t4 = kind === "relic" && <img src={imageUrl(cat.relics[item.id]?.image_url || `/static/images/relics/${item.id.toLowerCase()}.png`)} alt="" className="h-7 w-7 object-contain" loading="lazy" />;
    $[9] = cat;
    $[10] = item.id;
    $[11] = kind;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== cat || $[14] !== item.id || $[15] !== kind) {
    t5 = kind === "potion" && cat.potions[item.id]?.image_url && <img src={imageUrl(cat.potions[item.id].image_url)} alt="" className="h-7 w-7 object-contain" loading="lazy" />;
    $[13] = cat;
    $[14] = item.id;
    $[15] = kind;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== name) {
    t6 = <span className="min-w-0 flex-1 truncate">{name}</span>;
    $[17] = name;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== item.sale || $[20] !== t) {
    t7 = item.sale && <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-gold)]">{t("Sale")}</span>;
    $[19] = item.sale;
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== item.cost || $[23] !== t) {
    t8 = item.cost !== undefined && <span className="text-xs tabular-nums text-[var(--text-muted)]">{item.cost} {t("gold")}</span>;
    $[22] = item.cost;
    $[23] = t;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== bought || $[26] !== t) {
    t9 = bought && <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-gold)]">{t("Bought")}</span>;
    $[25] = bought;
    $[26] = t;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== cat || $[29] !== item.id || $[30] !== kind) {
    t10 = kind === "card" && <ScoreChip id={item.id} scores={cat.cardScores} />;
    $[28] = cat;
    $[29] = item.id;
    $[30] = kind;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== cat || $[33] !== item.id || $[34] !== kind) {
    t11 = kind === "relic" && <ScoreChip id={item.id} scores={cat.relicScores} />;
    $[32] = cat;
    $[33] = item.id;
    $[34] = kind;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t2 || $[39] !== t3 || $[40] !== t4 || $[41] !== t5 || $[42] !== t6 || $[43] !== t7 || $[44] !== t8 || $[45] !== t9) {
    t12 = <li className={t2}>{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}</li>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t2;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t8;
    $[45] = t9;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  return t12;
}
function ShopBlock(t0) {
  const $ = _c(19);
  const {
    f,
    prev,
    cat
  } = t0;
  const t = useT();
  useGameLocale();
  let t1;
  if ($[0] !== cat || $[1] !== f.goldAfter || $[2] !== f.lines || $[3] !== f.shop || $[4] !== prev?.goldAfter || $[5] !== t) {
    const buys = f.lines.filter(_temp5);
    const removed = f.lines.flatMap(_temp6);
    const goldIn = f.shop?.gold ?? prev?.goldAfter;
    const goldBuys = buys.filter(_temp7);
    const spentKnown = goldBuys.every(_temp8);
    const spent = goldBuys.reduce(_temp9, 0);
    const stock = f.shop;
    const groups = stock ? [["card", stock.cards], ["relic", stock.relics], ["potion", stock.potions]] as const : [];
    let t2;
    if ($[7] !== t) {
      t2 = t("Shop");
      $[7] = t;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    let t3;
    if ($[9] !== t2) {
      t3 = <h4 className="text-sm font-semibold text-[var(--text-primary)]">{t2}</h4>;
      $[9] = t2;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== goldIn || $[12] !== t) {
      t4 = goldIn !== undefined && <span>{t("Gold in")} {goldIn}</span>;
      $[11] = goldIn;
      $[12] = t;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== t) {
      t5 = t("Spent");
      $[14] = t;
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    let t6;
    if ($[16] !== f.goldAfter || $[17] !== t) {
      t6 = f.goldAfter !== undefined && <span>{t("Gold out")} {f.goldAfter}</span>;
      $[16] = f.goldAfter;
      $[17] = t;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t1 = <section className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] p-3"><header className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs tabular-nums text-[var(--text-muted)]">{t3}{t4}<span>{t5} {spentKnown ? spent : "?"}</span>{t6}</header>{stock && <div className="mb-3 space-y-2">{groups.map(t7 => {
          const [kind, items] = t7;
          return items.length ? <ul key={kind} className="grid gap-1.5 sm:grid-cols-2">{items.map(item => <StockRow key={`${kind}-${item.slot}`} item={item} kind={kind} bought={boughtSlots(buys, kind).has(item.slot)} cat={cat} />)}</ul> : null;
        })}{stock.removalCost !== undefined && <p className="text-xs text-[var(--text-muted)]">{t("Card removal")}: {stock.removalCost} {t("gold")}{removed.length > 0 && ` · ${t("Removed")} ${removed.map(id => cardName(id, cat)).join(", ")}`}</p>}</div>}{buys.length ? <ul className="grid gap-1.5 sm:grid-cols-2">{buys.map((l_4, i) => {
          const removal = l_4.kind === "removal_service";
          const target = removal ? removed[buys.slice(0, i).filter(_temp10).length] : l_4.id;
          const label = removal ? `${t("Card removal")}${target ? `: ${cardName(target, cat)}` : ""}` : itemName(l_4.kind, l_4.id ?? "", cat);
          return <li key={`${l_4.s}-${i}`} className="flex items-center gap-2 rounded-md border border-[var(--accent-gold)] bg-[color-mix(in_srgb,var(--accent-gold)_12%,transparent)] px-2.5 py-1.5 text-sm text-[var(--text-primary)]">{(l_4.kind === "card" || removal) && target && safeId(target) && <LiveCardImg id={target} alt={label} className="h-9 w-auto rounded-sm" portrait={cat.cards[target]?.image_url} />}{l_4.kind === "relic" && l_4.id && <img src={imageUrl(cat.relics[l_4.id]?.image_url || `/static/images/relics/${l_4.id.toLowerCase()}.png`)} alt="" className="h-7 w-7 object-contain" loading="lazy" />}{l_4.kind === "potion" && l_4.id && cat.potions[l_4.id]?.image_url && <img src={imageUrl(cat.potions[l_4.id].image_url)} alt="" className="h-7 w-7 object-contain" loading="lazy" />}<span className="min-w-0 flex-1 truncate">{label}</span><span className="text-xs tabular-nums text-[var(--text-muted)]">-{costLabel(l_4)}{l_4.goldOnHand !== undefined && ` · ${l_4.goldOnHand} ${t("after")}`}</span>{l_4.kind === "card" && l_4.id && <ScoreChip id={l_4.id} scores={cat.cardScores} />}{l_4.kind === "relic" && l_4.id && <ScoreChip id={l_4.id} scores={cat.relicScores} />}</li>;
        })}</ul> : <p className="text-sm text-[var(--text-muted)]">{t("Nothing bought")}</p>}</section>;
    $[0] = cat;
    $[1] = f.goldAfter;
    $[2] = f.lines;
    $[3] = f.shop;
    $[4] = prev?.goldAfter;
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
}
function _temp10(b) {
  return b.kind === "removal_service";
}
function _temp9(sum, l_3) {
  return sum + (l_3.costCurrent ?? 0);
}
function _temp8(l_2) {
  return l_2.costCurrent !== undefined;
}
function _temp7(l_1) {
  return l_1.costResource === "gold";
}
function _temp6(l_0) {
  return l_0.t === "remove" ? [l_0.id] : [];
}
function _temp5(l) {
  return l.t === "buy";
}
const BLOCK_ICON = "data:image/webp;base64,UklGRvwDAABXRUJQVlA4WAoAAAAQAAAAJwAAJwAAQUxQSFoBAAABkAPZtmlb69m2bduKbDOybdv2t23btm3b0bP3Xuc7jIgJgP+ALIZOij+FRPSSHR1ePIyJeNQe+DHxZmOmGScTbAquFVtfTkxMTIxcW5RmJYzEIWmT2rXz0fjE7D8uryoL1BHmpuGzCMpqWnvpywTxyJODi+vzY0yJuNNXHH3wfnSCfujD/fO9qiSizW8m8LfZkAhWP2ZggxkJa/hJvJFlBiTgsBPve7skkemKUbR3dYJEkm2f0e5lsBGxpd1HOxsG5B5H0DZZUeitGEb63CZJIVT2COlCPAsFuO3GGV5lB7RK7e9Q7pQIULGE7MUY2+AC9FKdHxGeVgsgsARtHaEa3ewNmII556gu5fKjgHzDPYo7DRqAbDf/HdH9Fj3AZvNd8obgTpcV4LN69d+f406LEQsDAKYt18dmXKg3AIYNi7Z9mPhxtFIHGBcKGDi8KEEWfkYDX302+HsEVlA4IHwCAABwDwCdASooACgAPikQhkKhoQ43bwAMAUJZADIF0cgDetugDDLwgHmA8//nZusA9ADyyPZXwAD+AdU+ub/xWcVyCBKA1Kv+q9Pr+x8vX0B7An8O/nn+r/M/vAeg0TdhHqLUFriUiZpcagPxZcPwm9IbFRLYYReMzvl6aHZ/eVYKFDy5eAAA/v+GF3qxhvvQ3hlZ/Ukn+bSLu80VFNzzErt/LJ1/lM//qL11t2uSFWPs0tzEon1GikCDU2/7FPNrlIHgVcsg/prbf+aWENCKy6+ftJgMfLD8GjAeu4eoC84waYL9O0mkXK9KATlmeP7mjydwvmU1ce7n6Wx1fjIIWy/6Qq4XCsf3//Fh3pywBotZh+3OGwn4ZvN22qHkNOZLj/l+wwZL01R7v2KmxCWOmuOnZveIQ5A4vfYzUu/7Wf4mFCPqE84XSJeyqE29pnc12rQl2+kCf+5YAeAszuniaLa6E/s/SwLDV9M0BMX3LaT43Ta075cEy5j/ErjRLJ/PVOEJkP/y4yY3at+KlXc4phWX/aj8Xc0U8AQv8Mh5239sG5sCYBsCRO/K20qg8fnMaEOs6aRaqJVAsQwRNdQKS60nbFr4ut0pXF387MeakpNduBdqBAEs8aZE0V/j6NQc33ASzFDCt/r/PZuykKp3g3P9ABL3c7LEMEZ7d5P5cdo14m0PFwQcwT9fCdJ5sUAb9tJOem5J/JFpw1f8/Y8ScHQ+ntEjFfig/6sKdRmzBq0e+kow9YKj5ztHZNpvfB997S6cfpnPxV2f8PWUMW9JNpsj+Fel1+ooV4CqP0x1y9ovUed8feQ3QOzeVuAKYkoELQR9+4nh5S8ht4DkAAA=";
function BlockChip(t0) {
  const $ = _c(7);
  const {
    n
  } = t0;
  const t = useT();
  let t1;
  if ($[0] !== t) {
    t1 = t("Block");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <img src={BLOCK_ICON} alt={t1} className="inline-block h-4 w-4 align-text-bottom" />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== n || $[5] !== t2) {
    t3 = <span className="inline-flex items-center gap-0.5 align-baseline tabular-nums">{n}{t2}</span>;
    $[4] = n;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function PowerChip(t0) {
  const $ = _c(12);
  const {
    id,
    n
  } = t0;
  let t1;
  if ($[0] !== id || $[1] !== n) {
    t1 = powerLabel(id, n);
    $[0] = id;
    $[1] = n;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== id) {
    t2 = id.toLowerCase();
    $[3] = id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `/static/images/powers/${t2}.webp`;
  let t4;
  if ($[5] !== t3) {
    t4 = imageUrl(t3);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <img src={t4} alt="" className="inline-block h-4 w-4 align-text-bottom" loading="lazy" onError={_temp11} />;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t5) {
    t6 = <span className="inline-flex items-center gap-0.5 align-baseline tabular-nums">{t1}{t5}</span>;
    $[9] = t1;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function _temp11(e) {
  (e.target as HTMLImageElement).style.display = "none";
}
const STAR_ICON = "https://cdn.spire-codex.com/game/v0.110.0/assets/ui/combat/energy_star.webp";
function Stars(t0) {
  const $ = _c(3);
  const {
    n
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <img src={STAR_ICON} alt="star" className="inline-block h-4 w-4 align-text-bottom" loading="lazy" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== n) {
    t2 = <span className="inline-flex items-center gap-0.5 align-baseline tabular-nums">{t1}{n}</span>;
    $[1] = n;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function Energy(t0) {
  const $ = _c(10);
  const {
    n,
    icon
  } = t0;
  const t = useT();
  const t1 = `/static/images/icons/${icon}_energy_icon.webp`;
  let t2;
  if ($[0] !== t1) {
    t2 = imageUrl(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t) {
    t3 = t("energy");
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <img src={t2} alt={t3} className="inline-block h-4 w-4 align-text-bottom" loading="lazy" />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== n || $[8] !== t4) {
    t5 = <span className="inline-flex items-center gap-0.5 align-baseline tabular-nums">{t4}{n}</span>;
    $[7] = n;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function powerLabel(id: string, n: number | undefined): string {
  return `${displayName(id.replace(/_POWER$/, ""))}${n !== undefined ? ` ${n}` : ""}`;
}
function TurnBlock(t0) {
  const $ = _c(49);
  const {
    turn,
    cat,
    energyIcon,
    playerId
  } = t0;
  const t = useT();
  const bp = useBetaPrefix();
  let t1;
  if ($[0] !== bp || $[1] !== cat || $[2] !== t) {
    t1 = (key, id, up) => withPill(t(key, {
      card: SLOT
    }), SLOT, <Card id={id} up={up} cat={cat} bp={bp} />);
    $[0] = bp;
    $[1] = cat;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cardPill = t1;
  let items;
  let lines;
  if ($[4] !== bp || $[5] !== cardPill || $[6] !== cat || $[7] !== energyIcon || $[8] !== playerId || $[9] !== t || $[10] !== turn.lines || $[11] !== turn.side) {
    items = [];
    lines = turn.lines;
    const claimed = new Set();
    let t2;
    if ($[14] !== playerId) {
      t2 = tgt => !tgt || tgt === "player" || cleanId(tgt).toUpperCase() === playerId;
      $[14] = playerId;
      $[15] = t2;
    } else {
      t2 = $[15];
    }
    const isSelf = t2;
    let t3;
    if ($[16] !== cat || $[17] !== isSelf || $[18] !== t || $[19] !== turn.side) {
      t3 = (id_0, n, tgt_0) => {
        const lost = n !== undefined && n < 0;
        const chip = <PowerChip id={id_0} n={lost ? -(n as number) : n} />;
        let text;
        if (isSelf(tgt_0)) {
          text = lost ? t("Lost {power}", {
            power: SLOT
          }) : turn.side === "player" ? t("Gained {power}", {
            power: SLOT
          }) : t("{power} applied to you", {
            power: SLOT
          });
        } else {
          const monster = monsterName(tgt_0 as string, cat);
          if (lost) {
            text = t("{monster} lost {power}", {
              monster,
              power: SLOT
            });
          } else {
            text = turn.side === "player" ? t("Applied {power} to {target}", {
              power: SLOT,
              target: monster
            }) : t("{monster} gained {power}", {
              monster,
              power: SLOT
            });
          }
        }
        return withPill(text, SLOT, chip);
      };
      $[16] = cat;
      $[17] = isSelf;
      $[18] = t;
      $[19] = turn.side;
      $[20] = t3;
    } else {
      t3 = $[20];
    }
    const powerChange = t3;
    if (turn.side === "player") {
      const drawn = lines.flatMap(_temp12);
      if (drawn.length) {
        let t4;
        if ($[21] !== t) {
          t4 = t("Drew");
          $[21] = t;
          $[22] = t4;
        } else {
          t4 = $[22];
        }
        let t5;
        if ($[23] !== bp || $[24] !== cat) {
          t5 = (l_0, i) => <span key={`${l_0.s}-${i}`}>{i > 0 && ", "}<Card id={l_0.id} cat={cat} bp={bp} /></span>;
          $[23] = bp;
          $[24] = cat;
          $[25] = t5;
        } else {
          t5 = $[25];
        }
        items.push(<>{t4}:{" "}{drawn.map(t5)}</>);
      }
      for (let i_0 = 0; i_0 < lines.length; i_0++) {
        const l_1 = lines[i_0];
        bb0: switch (l_1.t) {
          case "play":
            {
              const own = [];
              for (let j_0 = i_0 + 1; j_0 < lines.length && lines[j_0].t !== "play"; j_0++) {
                const h = lines[j_0];
                if (h.t === "hit" && h.src === "player" && (h.card ? h.card === l_1.id : true)) {
                  own.push(h);
                  claimed.add(j_0);
                }
              }
              const dmg = own.reduce(_temp13, 0);
              const killed = own.filter(_temp14).map(h_2 => monsterName(h_2.dst as string, cat));
              items.push(<>{t("Played")} <Card id={l_1.id} up={!!l_1.up} cat={cat} bp={bp} />{l_1.target && !isSelf(l_1.target) && <> → {monsterName(l_1.target, cat)}</>}{dmg > 0 && <>, {dmg} {t("damage")}</>}{killed.length > 0 && <>, {t("killed {who}", {
                    who: killed.join(", ")
                  })}</>}{l_1.costPaid !== undefined && <>{" "}· <Energy n={l_1.costPaid} icon={energyIcon} /></>}{l_1.starsPaid ? <>{" "}· <Stars n={l_1.starsPaid} /></> : null}</>);
              break bb0;
            }
          case "hit":
            {
              if (!claimed.has(i_0) && l_1.dst && l_1.dst !== "player" && l_1.dmg) {
                items.push(t("{target} took {n} damage", {
                  target: monsterName(l_1.dst, cat),
                  n: l_1.dmg
                }));
              }
              break bb0;
            }
          case "block":
            {
              if (l_1.n) {
                const card = l_1.card;
                items.push(<>{t("Block")} +<BlockChip n={l_1.n} />{card && <>{" "}(<Card id={card} cat={cat} bp={bp} />)</>}</>);
              }
              break bb0;
            }
          case "power":
            {
              items.push(powerChange(l_1.id, l_1.n, l_1.tgt));
              break bb0;
            }
          case "potion_used":
            {
              items.push(withPill(t("Used {potion}", {
                potion: SLOT
              }), SLOT, <Potion id={l_1.id} cat={cat} bp={bp} />));
              break bb0;
            }
          case "exhaust":
            {
              items.push(cardPill("Exhausted {card}", l_1.id));
              break bb0;
            }
          case "generate":
            {
              items.push(cardPill("Created {card}", l_1.id));
              break bb0;
            }
          case "pick":
            {
              if (l_1.cards.length === 1) {
                items.push(cardPill("Chose {card}", l_1.cards[0].id, !!l_1.cards[0].up));
              } else {
                if (l_1.cards.length > 1) {
                  items.push(<>{t("Chose")}:{" "}{l_1.cards.map((c, j) => <span key={`${l_1.s}-${j}`}>{j > 0 && ", "}<Card id={c.id} up={!!c.up} cat={cat} bp={bp} /></span>)}</>);
                }
              }
              break bb0;
            }
          case "hp":
            {
              if (l_1.d) {
                items.push(l_1.d > 0 ? t("Healed {n} HP", {
                  n: l_1.d
                }) : t("Lost {n} HP", {
                  n: -l_1.d
                }));
              }
            }
        }
      }
    } else {
      const generated = new Map();
      for (let i_1 = 0; i_1 < lines.length; i_1++) {
        const l_2 = lines[i_1];
        bb1: switch (l_2.t) {
          case "hit":
            {
              if (l_2.dmg === undefined) {
                break bb1;
              }
              if (l_2.dst === "player") {
                const src = l_2.src ?? "";
                const by = src === "effect" || !src ? t("An effect") : monsterName(src, cat);
                items.push(`${t("{monster} hit you for {n}", {
                  monster: by,
                  n: l_2.dmg
                })}${l_2.blocked ? ` (${l_2.blocked} ${t("blocked")})` : ""}`);
                for (let j_1 = i_1 - 1; j_1 >= 0 && j_1 >= i_1 - 2; j_1--) {
                  const h_3 = lines[j_1];
                  if (h_3.t === "hp" && h_3.d === -l_2.dmg && !claimed.has(j_1)) {
                    claimed.add(j_1);
                    break;
                  }
                }
              } else {
                if (l_2.dst && l_2.dmg) {
                  items.push(t("{target} took {n} damage", {
                    target: monsterName(l_2.dst, cat),
                    n: l_2.dmg
                  }));
                }
              }
              break bb1;
            }
          case "power":
            {
              items.push(powerChange(l_2.id, l_2.n, l_2.tgt));
              break bb1;
            }
          case "move":
            {
              items.push(t("{monster} used {move}", {
                monster: monsterName(l_2.src, cat),
                move: moveName(l_2.src, l_2.id, cat)
              }));
              break bb1;
            }
          case "block":
            {
              if (l_2.n && l_2.src && !isSelf(l_2.src) && l_2.src !== "effect") {
                items.push(withPill(t("{monster} gained {n} Block", {
                  monster: monsterName(l_2.src, cat),
                  n: SLOT
                }), SLOT, <BlockChip n={l_2.n} />));
              }
              break bb1;
            }
          case "generate":
            {
              generated.set(l_2.id, (generated.get(l_2.id) ?? 0) + 1);
              break bb1;
            }
          case "end":
            {
              items.push(l_2.terminalReason === "interrupted" ? t("The recording stopped here") : l_2.isGameOver ? t("You died here") : t("The run was left here"));
            }
        }
      }
      for (const [id_1, n_0] of generated) {
        items.push(n_0 > 1 ? withPill(t("Added {n} \xD7 {card} to your deck", {
          n: n_0,
          card: SLOT
        }), SLOT, <Card id={id_1} cat={cat} bp={bp} />) : cardPill("Added {card} to your deck", id_1));
      }
      lines.forEach((l_3, i_2) => {
        if (l_3.t === "hp" && l_3.d && !claimed.has(i_2)) {
          items.push(l_3.d > 0 ? t("Healed {n} HP", {
            n: l_3.d
          }) : t("Lost {n} HP", {
            n: -l_3.d
          }));
        }
      });
    }
    $[4] = bp;
    $[5] = cardPill;
    $[6] = cat;
    $[7] = energyIcon;
    $[8] = playerId;
    $[9] = t;
    $[10] = turn.lines;
    $[11] = turn.side;
    $[12] = items;
    $[13] = lines;
  } else {
    items = $[12];
    lines = $[13];
  }
  let t2;
  if ($[26] !== lines) {
    t2 = lines.findLast(_temp15);
    $[26] = lines;
    $[27] = t2;
  } else {
    t2 = $[27];
  }
  const hpLine = t2;
  const player = turn.side === "player";
  const start = turn.side === "start";
  const t3 = `text-xs font-semibold uppercase tracking-wider tabular-nums ${player ? "text-[var(--accent-gold)]" : start ? "text-[var(--text-muted)]" : "text-[var(--accent-red)]"}`;
  let t4;
  if ($[28] !== player || $[29] !== start || $[30] !== t || $[31] !== turn.n) {
    t4 = start ? t("Fight start") : `${player ? t("Turn") : t("Enemy")} ${turn.n}`;
    $[28] = player;
    $[29] = start;
    $[30] = t;
    $[31] = turn.n;
    $[32] = t4;
  } else {
    t4 = $[32];
  }
  let t5;
  if ($[33] !== t3 || $[34] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  let t6;
  if ($[36] !== hpLine) {
    t6 = hpLine && <span className="text-xs tabular-nums text-[var(--text-muted)]">HP {hpLine.hp}</span>;
    $[36] = hpLine;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] !== t5 || $[39] !== t6) {
    t7 = <div className="flex items-baseline gap-3">{t5}{t6}</div>;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  let t8;
  if ($[41] !== items || $[42] !== player || $[43] !== t || $[44] !== turn.linesLost) {
    t8 = items.length ? <ol className="mt-1 list-decimal space-y-0.5 pl-6 text-sm text-[var(--text-secondary)] marker:text-xs marker:tabular-nums marker:text-[var(--text-muted)]">{items.map(_temp16)}</ol> : <p className="mt-1 text-sm text-[var(--text-muted)]">{turn.linesLost ? t("{n} lines missing here", {
        n: turn.linesLost
      }) : player ? t("Nothing recorded") : t("No enemy action recorded")}</p>;
    $[41] = items;
    $[42] = player;
    $[43] = t;
    $[44] = turn.linesLost;
    $[45] = t8;
  } else {
    t8 = $[45];
  }
  let t9;
  if ($[46] !== t7 || $[47] !== t8) {
    t9 = <li className="py-2">{t7}{t8}</li>;
    $[46] = t7;
    $[47] = t8;
    $[48] = t9;
  } else {
    t9 = $[48];
  }
  return t9;
}

/** Names each fight on the floor. Two starts sharing a combat id are one fight
 * retried after a reload, not two fights, so they are numbered as attempts. */
function _temp16(it, i_3) {
  return <li key={i_3} className="pl-1">{it}</li>;
}
function _temp15(l_4) {
  return l_4.t === "hp";
}
function _temp14(h_1) {
  return h_1.killed && h_1.dst;
}
function _temp13(sum, h_0) {
  return sum + (h_0.dmg ?? 0);
}
function _temp12(l) {
  return l.t === "draw" ? [l] : [];
}
function combatLabels(combats: ReplayCombat[], t: TFn): (string | undefined)[] {
  const keys = combats.map((c, i) => c.combatId ?? `\u0000${i}`);
  const distinct = [...new Set(keys)];
  return keys.map((key, i) => {
    const fight = distinct.indexOf(key) + 1;
    const attempts = keys.filter(k => k === key).length;
    if (attempts > 1) return t("Fight {n}, attempt {k}", {
      n: fight,
      k: keys.slice(0, i + 1).filter(k => k === key).length
    });
    return distinct.length > 1 ? t("Fight {n} of {of}", {
      n: fight,
      of: distinct.length
    }) : undefined;
  });
}
function CombatBlock(t0) {
  const $ = _c(65);
  const {
    c,
    label,
    cat,
    energyIcon,
    playerId
  } = t0;
  const t = useT();
  useGameLocale();
  let t1;
  if ($[0] !== c.enemies || $[1] !== cat) {
    let t2;
    if ($[3] !== cat) {
      t2 = e => <img key={`${e.i}-${e.id}`} src={imageUrl(cat.monsters[cleanId(e.id)]?.image_url || `/static/images/monsters/${e.id.toLowerCase()}.webp`)} alt={monsterName(e.id, cat)} title={`${monsterName(e.id, cat)} ${e.hp}/${e.maxHp}`} className="h-10 w-10 rounded-full border-2 border-[var(--bg-card)] object-cover" loading="lazy" />;
      $[3] = cat;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = c.enemies.map(t2);
    $[0] = c.enemies;
    $[1] = cat;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex -space-x-2">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== label) {
    t3 = label && <span className="mr-1.5 text-xs text-[var(--text-muted)]">{label}</span>;
    $[7] = label;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== c.enemies || $[10] !== cat) {
    let t5;
    if ($[12] !== cat) {
      t5 = e_0 => monsterName(e_0.id, cat);
      $[12] = cat;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = c.enemies.map(t5).join(", ");
    $[9] = c.enemies;
    $[10] = cat;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[14] !== t3 || $[15] !== t4) {
    t5 = <div className="font-semibold text-[var(--text-primary)]">{t3}{t4}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== c.endRecorded || $[18] !== c.result || $[19] !== t) {
    t6 = c.result ? <><span className={c.result === "victory" ? "text-[var(--accent-gold)]" : "text-[var(--accent-red)]"}>{t(c.result === "victory" ? "Victory" : c.result === "death" ? "Died" : displayName(c.result))}</span>{" \xB7 "}</> : <><span className="text-[var(--text-muted)]">{t(c.endRecorded ? "Result not recorded" : "End not recorded")}</span>{" \xB7 "}</>;
    $[17] = c.endRecorded;
    $[18] = c.result;
    $[19] = t;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== c.turnCount || $[22] !== c.turns) {
    t7 = c.turnCount ?? c.turns.filter(_temp17).length;
    $[21] = c.turnCount;
    $[22] = c.turns;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t) {
    t8 = t("turns");
    $[24] = t;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== c.rolledBackByReload || $[27] !== c.supersededByRetry || $[28] !== t) {
    t9 = (c.supersededByRetry || c.rolledBackByReload) && <><span className="text-[var(--text-muted)]">{t("Undone by a reload, so this attempt did not stick")}</span>{" \xB7 "}</>;
    $[26] = c.rolledBackByReload;
    $[27] = c.supersededByRetry;
    $[28] = t;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== c.resumedAcrossReload || $[31] !== t) {
    t10 = c.resumedAcrossReload && <><span className="text-[var(--text-muted)]">{t("Continued after a reload")}</span>{" \xB7 "}</>;
    $[30] = c.resumedAcrossReload;
    $[31] = t;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== c.hpLossRecorded || $[34] !== c.hpLost || $[35] !== t) {
    t11 = c.hpLost !== undefined ? `${c.hpLost} ${t("HP lost")}` : c.hpLossRecorded !== undefined ? t("At least {n} HP lost", {
      n: c.hpLossRecorded
    }) : t("HP lost unknown");
    $[33] = c.hpLossRecorded;
    $[34] = c.hpLost;
    $[35] = t;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const t12 = c.hpEnd !== undefined && ` · HP ${c.hpEnd}`;
  let t13;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t6 || $[41] !== t7 || $[42] !== t8 || $[43] !== t9) {
    t13 = <div className="text-xs text-[var(--text-muted)]">{t6}{t7}{" "}{t8}{t9}{t10}{" \xB7 "}{t11}{t12}</div>;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t6;
    $[41] = t7;
    $[42] = t8;
    $[43] = t9;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t13 || $[46] !== t5) {
    t14 = <div className="text-sm">{t5}{t13}</div>;
    $[45] = t13;
    $[46] = t5;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== t14 || $[49] !== t2) {
    t15 = <header className="mb-2 flex flex-wrap items-center gap-3">{t2}{t14}</header>;
    $[48] = t14;
    $[49] = t2;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== c.turns || $[52] !== cat || $[53] !== energyIcon || $[54] !== playerId) {
    let t17;
    if ($[56] !== cat || $[57] !== energyIcon || $[58] !== playerId) {
      t17 = (tn, i) => <TurnBlock key={`${tn.side}-${tn.n}-${i}`} turn={tn} cat={cat} energyIcon={energyIcon} playerId={playerId} />;
      $[56] = cat;
      $[57] = energyIcon;
      $[58] = playerId;
      $[59] = t17;
    } else {
      t17 = $[59];
    }
    t16 = c.turns.map(t17);
    $[51] = c.turns;
    $[52] = cat;
    $[53] = energyIcon;
    $[54] = playerId;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[60] !== t16) {
    t17 = <ol className="divide-y divide-[var(--border-subtle)]">{t16}</ol>;
    $[60] = t16;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== t15 || $[63] !== t17) {
    t18 = <section className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] p-3">{t15}{t17}</section>;
    $[62] = t15;
    $[63] = t17;
    $[64] = t18;
  } else {
    t18 = $[64];
  }
  return t18;
}
function _temp17(x) {
  return x.side === "player";
}
function LootLine(t0) {
  const $ = _c(2);
  const {
    f,
    cat,
    who
  } = t0;
  const t = useT();
  const chosen = new Set(f.decisions.flatMap(_temp21));
  const rewarded = new Set(f.decisions.flatMap(_temp23));
  const decided = new Set(f.decisions.flatMap(d_1 => d_1.resolutions.filter(r_0 => r_0.t === "resolve" || (r_0.t === "acquire" || r_0.t === "remove" || r_0.t === "upgrade") && chosen.has(r_0.id) || r_0.t === "transform" && chosen.has(r_0.fromId) || r_0.t === "relic" && (chosen.has(r_0.id) || rewarded.has(r_0.id))).map(_temp24)));
  let t1;
  if ($[0] !== f.lines) {
    t1 = f.lines.find(_temp25);
    $[0] = f.lines;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const restHeal = t1;
  const heals = restHeal ? f.lines.filter(_temp26) : [];
  const healLine = heals.findLast(l_1 => l_1.s < restHeal.s) ?? heals[0];
  const bp = useBetaPrefix();
  const counts = new Map();
  for (const l_2 of f.lines) {
    if (decided.has(l_2.s)) {
      continue;
    }
    if (isShopKind(f.kind) && (l_2.t === "buy" || l_2.t === "remove" || l_2.t === "acquire")) {
      continue;
    }
    if (l_2.t === "resume" || l_2.t === "hp_loss") {
      continue;
    }
    if (l_2.t === "potion_got" && rewarded.has(l_2.id)) {
      continue;
    }
    if (f.combats.length && l_2.t === "hp") {
      continue;
    }
    if (l_2 === healLine) {
      continue;
    }
    const d_2 = describeLine(l_2, cat, t, who, bp, l_2 === restHeal && healLine?.t === "hp" ? healLine.d : undefined);
    if (!d_2) {
      continue;
    }
    const hit = counts.get(d_2.key);
    if (hit) {
      hit.n = hit.n + 1;
    } else {
      counts.set(d_2.key, {
        node: d_2.node,
        n: 1
      });
    }
  }
  if (!counts.size) {
    return null;
  }
  return <ol className="list-decimal space-y-0.5 pl-6 text-sm text-[var(--text-secondary)] marker:text-xs marker:tabular-nums marker:text-[var(--text-muted)]">{[...counts].map(_temp27)}</ol>;
}
function _temp27(t0) {
  const [key, t1] = t0;
  const {
    node,
    n
  } = t1;
  return <li key={key} className="pl-1">{node}{n > 1 ? ` ×${n}` : ""}</li>;
}
function _temp26(l_0) {
  return l_0.t === "hp" && (l_0.d ?? 0) > 0 && (l_0.src === undefined || l_0.src === "heal");
}
function _temp25(l) {
  return l.t === "rest" && l.option === "heal";
}
function _temp24(r_1) {
  return r_1.s;
}
function _temp23(d_0) {
  return d_0.resolutions.flatMap(_temp22);
}
function _temp22(r) {
  return r.t === "resolve" && r.id ? [r.id] : r.t === "relic" ? [r.id] : [];
}
function _temp21(d) {
  return d.options.filter(_temp18).flatMap(_temp20);
}
function _temp20(o_0) {
  return [o_0.id, o_0.grantsRelic].filter(_temp19);
}
function _temp19(x) {
  return !!x;
}
function _temp18(o) {
  return o.chosen;
}
export default function FloorPanel(t0) {
  const $ = _c(96);
  const {
    f,
    prev,
    cat,
    maxHp,
    who,
    energyIcon,
    playerId,
    recordingEndsHere
  } = t0;
  const t = useT();
  useGameLocale();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== cat || $[1] !== energyIcon || $[2] !== f || $[3] !== maxHp || $[4] !== playerId || $[5] !== prev || $[6] !== t || $[7] !== who) {
    const visibleDecisions = f.decisions.filter(_temp28);
    let t7;
    if ($[14] !== f.combats || $[15] !== t) {
      t7 = combatLabels(f.combats, t);
      $[14] = f.combats;
      $[15] = t;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const combatNames = t7;
    const hpDelta = prev?.hpAfter !== undefined && f.hpAfter !== undefined ? f.hpAfter - prev.hpAfter : 0;
    const goldDelta = prev?.goldAfter !== undefined && f.goldAfter !== undefined ? f.goldAfter - prev.goldAfter : 0;
    t1 = "space-y-3";
    let t8;
    if ($[17] !== t) {
      t8 = t("Floor");
      $[17] = t;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    const t9 = f.floor;
    let t10;
    if ($[19] !== cat || $[20] !== f || $[21] !== t) {
      t10 = floorTitle(f, cat, t);
      $[19] = cat;
      $[20] = f;
      $[21] = t;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== f.floor || $[24] !== t10 || $[25] !== t8) {
      t11 = <h3 className="text-lg font-semibold text-[var(--text-primary)]">{t8} {t9} · {t10}</h3>;
      $[23] = f.floor;
      $[24] = t10;
      $[25] = t8;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    const t12 = KIND_LABEL[f.kind] ?? f.kind;
    let t13;
    if ($[27] !== t || $[28] !== t12) {
      t13 = t(t12);
      $[27] = t;
      $[28] = t12;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== t13) {
      t14 = <span className="text-xs text-[var(--text-muted)]">{t13}</span>;
      $[30] = t13;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== f.resumes || $[33] !== t) {
      t15 = f.resumes.length > 0 && <span className="rounded-md border border-[var(--accent-red)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--accent-red)]">{t("Reloaded from a save")}{f.resumes[f.resumes.length - 1].reloads > 1 ? ` ×${f.resumes[f.resumes.length - 1].reloads}` : ""}</span>;
      $[32] = f.resumes;
      $[33] = t;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== f.hpAfter || $[36] !== hpDelta || $[37] !== maxHp) {
      t16 = f.hpAfter !== undefined && <>HP {f.hpAfter}{maxHp ? `/${maxHp}` : ""}<Delta value={hpDelta} /></>;
      $[35] = f.hpAfter;
      $[36] = hpDelta;
      $[37] = maxHp;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] !== f.goldAfter || $[40] !== goldDelta || $[41] !== t) {
      t17 = f.goldAfter !== undefined && <>{" "}· {f.goldAfter} {t("gold")}<Delta value={goldDelta} /></>;
      $[39] = f.goldAfter;
      $[40] = goldDelta;
      $[41] = t;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    let t18;
    if ($[43] !== t16 || $[44] !== t17) {
      t18 = <span className="ml-auto text-xs tabular-nums text-[var(--text-muted)]">{t16}{t17}</span>;
      $[43] = t16;
      $[44] = t17;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    if ($[46] !== t11 || $[47] !== t14 || $[48] !== t15 || $[49] !== t18) {
      t2 = <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">{t11}{t14}{t15}{t18}</header>;
      $[46] = t11;
      $[47] = t14;
      $[48] = t15;
      $[49] = t18;
      $[50] = t2;
    } else {
      t2 = $[50];
    }
    if ($[51] !== f.linesLost || $[52] !== t) {
      t3 = f.linesLost > 0 && <p className="rounded-md border border-[var(--border-subtle)] px-3 py-2 text-xs text-[var(--text-muted)]">{t("{n} lines from this floor are missing from the record, so something here went unrecorded.", {
          n: f.linesLost
        })}</p>;
      $[51] = f.linesLost;
      $[52] = t;
      $[53] = t3;
    } else {
      t3 = $[53];
    }
    if ($[54] !== cat || $[55] !== combatNames || $[56] !== energyIcon || $[57] !== f.combats || $[58] !== playerId) {
      let t19;
      if ($[60] !== cat || $[61] !== combatNames || $[62] !== energyIcon || $[63] !== playerId) {
        t19 = (c, i) => <CombatBlock key={`${c.combatId ?? c.encounter}-${i}`} c={c} label={combatNames[i]} cat={cat} energyIcon={energyIcon} playerId={playerId} />;
        $[60] = cat;
        $[61] = combatNames;
        $[62] = energyIcon;
        $[63] = playerId;
        $[64] = t19;
      } else {
        t19 = $[64];
      }
      t4 = f.combats.map(t19);
      $[54] = cat;
      $[55] = combatNames;
      $[56] = energyIcon;
      $[57] = f.combats;
      $[58] = playerId;
      $[59] = t4;
    } else {
      t4 = $[59];
    }
    if ($[65] !== cat || $[66] !== f || $[67] !== prev) {
      t5 = isShopKind(f.kind) && <ShopBlock f={f} prev={prev} cat={cat} />;
      $[65] = cat;
      $[66] = f;
      $[67] = prev;
      $[68] = t5;
    } else {
      t5 = $[68];
    }
    let t19;
    if ($[69] !== cat || $[70] !== who) {
      t19 = d_0 => <DecisionCard key={d_0.id} d={d_0} cat={cat} who={who} />;
      $[69] = cat;
      $[70] = who;
      $[71] = t19;
    } else {
      t19 = $[71];
    }
    t6 = visibleDecisions.map(t19);
    $[0] = cat;
    $[1] = energyIcon;
    $[2] = f;
    $[3] = maxHp;
    $[4] = playerId;
    $[5] = prev;
    $[6] = t;
    $[7] = who;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[72] !== cat || $[73] !== f || $[74] !== who) {
    t7 = <LootLine f={f} cat={cat} who={who} />;
    $[72] = cat;
    $[73] = f;
    $[74] = who;
    $[75] = t7;
  } else {
    t7 = $[75];
  }
  let t8;
  if ($[76] !== f.combats.length || $[77] !== f.decisions.length || $[78] !== f.lines || $[79] !== recordingEndsHere || $[80] !== t) {
    t8 = !f.combats.length && !f.decisions.length && !f.lines.some(_temp29) && <p className="text-sm text-[var(--text-muted)]">{recordingEndsHere ? t("The recording ends on this floor, so what happened here was never written.") : t("Nothing else was recorded on this floor.")}</p>;
    $[76] = f.combats.length;
    $[77] = f.decisions.length;
    $[78] = f.lines;
    $[79] = recordingEndsHere;
    $[80] = t;
    $[81] = t8;
  } else {
    t8 = $[81];
  }
  let t9;
  if ($[82] !== f.lines || $[83] !== recordingEndsHere || $[84] !== t) {
    t9 = recordingEndsHere && f.lines.some(_temp30) && <p className="text-sm text-[var(--text-muted)]">{t("The recording ends on this floor.")}</p>;
    $[82] = f.lines;
    $[83] = recordingEndsHere;
    $[84] = t;
    $[85] = t9;
  } else {
    t9 = $[85];
  }
  let t10;
  if ($[86] !== t1 || $[87] !== t2 || $[88] !== t3 || $[89] !== t4 || $[90] !== t5 || $[91] !== t6 || $[92] !== t7 || $[93] !== t8 || $[94] !== t9) {
    t10 = <div className={t1}>{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[86] = t1;
    $[87] = t2;
    $[88] = t3;
    $[89] = t4;
    $[90] = t5;
    $[91] = t6;
    $[92] = t7;
    $[93] = t8;
    $[94] = t9;
    $[95] = t10;
  } else {
    t10 = $[95];
  }
  return t10;
}
function _temp30(l_0) {
  return ["relic", "acquire", "potion_got", "upgrade", "remove", "transform", "rest", "buy"].includes(l_0.t);
}
function _temp29(l) {
  return ["relic", "acquire", "potion_got", "upgrade", "remove", "transform", "rest", "buy"].includes(l.t);
}
function _temp28(d) {
  return !(d.type === "event" && d.nPresented === 0 && d.resolutions.length === 0);
}